import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import { staffTabClass, staffChipClass, staffCardClass, staffSecondaryBtnClass, staffInputClass, staffTableWrapClass, chartTooltipStyle } from '../components/layout/staffPageStyles';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  Globe, Users, Eye, Monitor, MapPin, Clock,
  ChevronLeft, ChevronRight, Search, Filter, RefreshCw, Loader2, TrendingUp,
  ShieldBan, ShieldCheck, Ban, X, AlertTriangle, Bell, BellRing, Shield, Settings2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SITE_COLORS = {
  'remobiles.com': '#7c3aed',
  'guyarepair.com': '#22c55e',
  'soudurejvc.com': '#f59e0b',
};

const PERIODS = [
  { id: 'today', labelKey: 'analytics.period_today' },
  { id: '7d', labelKey: 'analytics.period_7d' },
  { id: '30d', labelKey: 'analytics.period_30d' },
  { id: '90d', labelKey: 'analytics.period_90d' },
  { id: '365d', labelKey: 'analytics.period_365d' },
];

export default function AnalyticsPage() {
  const { api } = useAuth();
  const { t, formatNumber, formatDate } = useLanguage();
  const [dashboard, setDashboard] = useState(null);
  const [visits, setVisits] = useState([]);
  const [visitsTotal, setVisitsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [siteFilter, setSiteFilter] = useState('all');
  const [visitsPage, setVisitsPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [tab, setTab] = useState('dashboard');
  const [blockedIps, setBlockedIps] = useState([]);
  const [blockingIp, setBlockingIp] = useState(null);
  const [confirmBlock, setConfirmBlock] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [alertConfig, setAlertConfig] = useState(null);
  const [alertHistory, setAlertHistory] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);
  const [checkingNow, setCheckingNow] = useState(false);

  const fetchBlockedIps = useCallback(async () => {
    try {
      const res = await api.get('/analytics/blocked-ips');
      setBlockedIps(res.data.blocked_ips || []);
    } catch {}
  }, [api]);

  const fetchAlertConfig = useCallback(async () => {
    try {
      const res = await api.get('/analytics/ip-alerts/config');
      setAlertConfig(res.data);
    } catch {}
  }, [api]);

  const fetchAlertHistory = useCallback(async () => {
    try {
      const res = await api.get('/analytics/ip-alerts/history');
      setAlertHistory(res.data.alerts || []);
    } catch {}
  }, [api]);

  const saveAlertConfig = async () => {
    setSavingConfig(true);
    try {
      await api.post('/analytics/ip-alerts/config', alertConfig);
    } catch {}
    setSavingConfig(false);
  };

  const checkNow = async () => {
    setCheckingNow(true);
    try {
      const res = await api.post('/analytics/ip-alerts/check-now');
      const sus = res.data.suspicious_ips || [];
      if (sus.length > 0) {
        alert(t('analytics.alert_suspicious_found', { count: sus.length }));
      } else {
        alert(t('analytics.alert_no_suspicious'));
      }
      fetchAlertHistory();
    } catch {}
    setCheckingNow(false);
  };

  const handleBlockIp = async (ip) => {
    setBlockingIp(ip);
    try {
      await api.post('/analytics/block-ip', { ip, reason: blockReason });
      setConfirmBlock(null);
      setBlockReason('');
      fetchBlockedIps();
      if (tab === 'visits') fetchVisits(visitsPage);
    } catch (err) {
      console.error('Block IP error:', err);
    } finally {
      setBlockingIp(null);
    }
  };

  const handleUnblockIp = async (ip) => {
    setBlockingIp(ip);
    try {
      await api.post('/analytics/unblock-ip', { ip });
      fetchBlockedIps();
    } catch (err) {
      console.error('Unblock IP error:', err);
    } finally {
      setBlockingIp(null);
    }
  };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (siteFilter !== 'all') params.set('site', siteFilter);
      const res = await api.get(`/analytics/dashboard?${params}`);
      setDashboard(res.data);
    } catch (err) {
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  }, [api, period, siteFilter]);

  const fetchVisits = useCallback(async (pg = 1) => {
    try {
      const params = new URLSearchParams({ page: pg, limit: 50 });
      if (siteFilter !== 'all') params.set('site', siteFilter);
      if (searchQuery) params.set('search', searchQuery);
      const res = await api.get(`/analytics/visits?${params}`);
      setVisits(res.data.visits || []);
      setVisitsTotal(res.data.total || 0);
      setVisitsPage(pg);
    } catch {}
  }, [api, siteFilter, searchQuery]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { if (tab === 'visits') { fetchVisits(1); fetchBlockedIps(); } }, [tab, fetchVisits, fetchBlockedIps]);
  useEffect(() => { if (tab === 'blocked') fetchBlockedIps(); }, [tab, fetchBlockedIps]);
  useEffect(() => { if (tab === 'alerts') { fetchAlertConfig(); fetchAlertHistory(); } }, [tab, fetchAlertConfig, fetchAlertHistory]);

  const d = dashboard;
  const totalPages = Math.ceil(visitsTotal / 50);

  const fmtDate = (iso) => {
    if (!iso) return '';
    return formatDate(iso, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Layout>
      <div className="space-y-5 max-w-7xl mx-auto" data-testid="analytics-page">
        <PageHeader
          title={t('analytics.page_title')}
          subtitle={t('analytics.page_subtitle')}
          backTo="/dashboard"
          icon={Globe}
          testId="analytics-page-header"
          actions={(
            <button type="button" onClick={() => { fetchDashboard(); if (tab === 'visits') fetchVisits(1); }}
              className={staffSecondaryBtnClass}
              data-testid="refresh-analytics">
              <RefreshCw className="w-4 h-4" /> {t('analytics.refresh')}
            </button>
          )}
        />

          {/* Tabs */}
          <div className="flex items-center gap-2 flex-wrap">
            {[{ id: 'dashboard', label: t('analytics.tab_dashboard'), icon: TrendingUp }, { id: 'visits', label: t('analytics.tab_visits'), icon: Eye }, { id: 'blocked', label: t('analytics.tab_blocked'), icon: ShieldBan }, { id: 'alerts', label: t('analytics.tab_alerts'), icon: BellRing }].map(tb => (
              <button key={tb.id} type="button" onClick={() => setTab(tb.id)}
                className={staffTabClass(tab === tb.id)}
                data-testid={`tab-${tb.id}`}>
                <tb.icon className="w-4 h-4" /> {tb.label}
              </button>
            ))}
          </div>

          {/* Period + Site filter */}
          <div className="flex flex-wrap items-center gap-2">
            {PERIODS.map(p => (
              <button key={p.id} type="button" onClick={() => setPeriod(p.id)}
                className={staffChipClass(period === p.id)}
                data-testid={`period-${p.id}`}>
                {t(p.labelKey)}
              </button>
            ))}
            <span className="text-gray-600 mx-1">|</span>
            {['all', 'remobiles.com', 'guyarepair.com', 'soudurejvc.com'].map(s => (
              <button key={s} type="button" onClick={() => setSiteFilter(s)}
                className={siteFilter === s && s !== 'all'
                  ? 'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border'
                  : staffChipClass(siteFilter === s)}
                style={siteFilter === s && s !== 'all' ? { background: `${SITE_COLORS[s]}22`, color: SITE_COLORS[s], borderColor: `${SITE_COLORS[s]}55` } : undefined}
                data-testid={`site-${s}`}>
                {s === 'all' ? t('analytics.all_sites') : s}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[var(--theme-primary)]" />
            </div>
          ) : tab === 'dashboard' ? (
            <div className="space-y-5">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: t('analytics.kpi_total_visits'), value: d?.total_visits || 0, icon: Eye, color: 'var(--theme-primary)' },
                  { label: t('analytics.kpi_unique_visitors'), value: d?.unique_visitors || 0, icon: Users, color: '#22c55e' },
                  { label: t('analytics.kpi_active_sites'), value: d?.by_site?.length || 0, icon: Globe, color: '#f59e0b' },
                  { label: t('analytics.kpi_countries'), value: d?.by_country?.length || 0, icon: MapPin, color: '#ef4444' },
                ].map((kpi, i) => (
                  <div key={i} className={`${staffCardClass} p-4`} data-testid={`kpi-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <kpi.icon className="w-4 h-4" style={{ color: kpi.color }} />
                      <span className="text-[11px] font-medium text-gray-500">{kpi.label}</span>
                    </div>
                    <p className="text-2xl font-black text-white">{formatNumber(kpi.value)}</p>
                  </div>
                ))}
              </div>

              {/* Daily visits chart */}
              {d?.daily?.length > 0 && (
                <div className={`${staffCardClass} p-5`}>
                  <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-[var(--theme-primary)]" /> {t('analytics.visits_per_day')}
                  </h3>
                  <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                      <AreaChart data={d.daily} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="visitGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="var(--theme-primary)" stopOpacity={0.3} />
                            <stop offset="100%" stopColor="var(--theme-primary)" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }}
                          tickFormatter={v => v ? v.substring(5) : ''} />
                        <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                        <Tooltip contentStyle={chartTooltipStyle} />
                        <Area type="monotone" dataKey="visits" stroke="var(--theme-primary)" strokeWidth={2} fill="url(#visitGrad)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* By site + By country */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* By Site */}
                {d?.by_site?.length > 0 && (
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                    <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-400" /> {t('analytics.by_site')}
                    </h3>
                    <div style={{ width: '100%', height: 250 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie data={d.by_site.map(s => ({ ...s, name: s.site, value: s.visits }))}
                            dataKey="value" cx="50%" cy="50%" innerRadius={45} outerRadius={85}
                            paddingAngle={3}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            labelLine={{ stroke: '#475569' }}>
                            {d.by_site.map((s, i) => (
                              <Cell key={i} fill={SITE_COLORS[s.site] || '#6b7280'} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={chartTooltipStyle} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* By Country */}
                {d?.by_country?.length > 0 && (
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                    <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-red-400" /> {t('analytics.by_country')}
                    </h3>
                    <div className="space-y-2 max-h-[250px] overflow-y-auto">
                      {d.by_country.map((c, i) => {
                        const maxV = d.by_country[0]?.visits || 1;
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-xs font-medium w-32 truncate" style={{ color: '#e2e8f0' }}>{c.country}</span>
                            <div className="flex-1 h-5 rounded-full overflow-hidden bg-white/10">
                              <div className="h-full rounded-full transition-all bg-[var(--theme-primary)]"
                                style={{ width: `${(c.visits / maxV) * 100}%`, opacity: 0.85 }} />
                            </div>
                            <span className="text-xs font-bold text-white w-12 text-right">{c.visits}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Top pages + Visits by hour */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Top pages */}
                {d?.top_pages?.length > 0 && (
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                    <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-amber-400" /> {t('analytics.top_pages')}
                    </h3>
                    <div className="space-y-2 max-h-[250px] overflow-y-auto">
                      {d.top_pages.map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1.5 px-2 rounded-lg hover:bg-white/5">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                              style={{ background: SITE_COLORS[p.site] || '#6b7280', color: '#fff' }}>
                              {i + 1}
                            </span>
                            <span className="truncate" style={{ color: '#cbd5e1' }}>{p.site}{p.page}</span>
                          </div>
                          <span className="font-bold text-white ml-2 shrink-0">{p.visits}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By hour */}
                {d?.by_hour?.length > 0 && (
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                    <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-cyan-400" /> {t('analytics.visits_per_hour')}
                    </h3>
                    <div style={{ width: '100%', height: 250 }}>
                      <ResponsiveContainer>
                        <BarChart data={d.by_hour} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                          <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={v => `${v}h`} />
                          <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                          <Tooltip contentStyle={chartTooltipStyle} />
                          <Bar dataKey="visits" fill="#22c55e" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>

              {/* Empty state */}
              {(!d?.total_visits || d.total_visits === 0) && (
                <div className="text-center py-16">
                  <Globe className="w-12 h-12 mx-auto mb-3" style={{ color: '#334155' }} />
                  <p className="text-sm font-medium text-gray-500">{t('analytics.empty_no_visits')}</p>
                  <p className="text-xs mt-1 text-gray-600">{t('analytics.empty_install_script')}</p>
                </div>
              )}
            </div>
          ) : tab === 'visits' ? (
            /* ─── Visits table ─── */
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); }}
                    placeholder={t('analytics.search_placeholder')}
                    className={`${staffInputClass} pl-10`}
                    data-testid="visits-search" />
                </div>
                <button type="button" onClick={() => fetchVisits(1)} className={staffSecondaryBtnClass}>
                  <Filter className="w-4 h-4" />
                </button>
              </div>

              <div className={staffTableWrapClass}>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-white/5">
                        <th className="text-left px-4 py-3 font-semibold text-gray-400">{t('analytics.col_date')}</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-400">{t('analytics.col_site')}</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-400">{t('analytics.col_page')}</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-400">IP</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-400">{t('analytics.col_country_city')}</th>
                        <th className="text-left px-4 py-3 font-semibold hidden md:table-cell" style={{ color: '#94a3b8' }}>{t('analytics.col_screen')}</th>
                        <th className="text-center px-4 py-3 font-semibold" style={{ color: '#94a3b8' }}>{t('analytics.col_action')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visits.length === 0 ? (
                        <tr><td colSpan={7} className="text-center py-12" style={{ color: '#475569' }}>{t('analytics.no_visits')}</td></tr>
                      ) : visits.map((v, i) => {
                        const isBlocked = blockedIps.some(b => b.ip === v.ip && b.active);
                        return (
                        <tr key={i} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                          <td className="px-4 py-2.5" style={{ color: '#94a3b8' }}>{fmtDate(v.timestamp)}</td>
                          <td className="px-4 py-2.5">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                              style={{ background: `${SITE_COLORS[v.site] || '#6b7280'}22`, color: SITE_COLORS[v.site] || '#6b7280' }}>
                              {v.site}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 max-w-[200px] truncate" style={{ color: '#cbd5e1' }}>{v.page}</td>
                          <td className="px-4 py-2.5 font-mono" style={{ color: isBlocked ? '#ef4444' : '#60a5fa' }}>
                            {v.ip}
                            {isBlocked && <span className="ml-1 text-[9px] text-red-400 font-bold">{t('analytics.blocked_inline')}</span>}
                          </td>
                          <td className="px-4 py-2.5" style={{ color: '#e2e8f0' }}>
                            {v.country}{v.city ? `, ${v.city}` : ''}
                          </td>
                          <td className="px-4 py-2.5 hidden md:table-cell" style={{ color: '#64748b' }}>{v.screen}</td>
                          <td className="px-4 py-2.5 text-center">
                            {isBlocked ? (
                              <button onClick={() => handleUnblockIp(v.ip)}
                                disabled={blockingIp === v.ip}
                                className="px-2 py-1 rounded-lg text-[10px] font-bold transition-all hover:scale-105 disabled:opacity-50"
                                style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }}
                                data-testid={`unblock-ip-${v.ip}`}>
                                {blockingIp === v.ip ? '...' : t('analytics.unblock')}
                              </button>
                            ) : (
                              <button onClick={() => setConfirmBlock(v.ip)}
                                disabled={blockingIp === v.ip}
                                className="px-2 py-1 rounded-lg text-[10px] font-bold transition-all hover:scale-105 disabled:opacity-50"
                                style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}
                                data-testid={`block-ip-${v.ip}`}>
                                <Ban className="w-3 h-3 inline mr-1" />{t('analytics.block')}
                              </button>
                            )}
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
                    <span className="text-xs" style={{ color: '#64748b' }}>{t('analytics.visits_count', { count: visitsTotal })}</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => fetchVisits(visitsPage - 1)} disabled={visitsPage <= 1}
                        className="p-1.5 rounded-lg disabled:opacity-30" style={{ background: '#1e293b', color: '#94a3b8' }}>
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-xs font-medium text-white">{visitsPage}/{totalPages}</span>
                      <button onClick={() => fetchVisits(visitsPage + 1)} disabled={visitsPage >= totalPages}
                        className="p-1.5 rounded-lg disabled:opacity-30" style={{ background: '#1e293b', color: '#94a3b8' }}>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* ─── Blocked IPs tab ─── */
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-white flex items-center gap-2">
                  <ShieldBan className="w-4 h-4 text-red-400" /> {t('analytics.blocked_ips_title')}
                </h2>
                <span className="text-xs px-2 py-1 rounded-lg" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>
                  {t('analytics.active_count', { count: blockedIps.filter(b => b.active).length })}
                </span>
              </div>

              <div className={staffTableWrapClass}>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ background: '#1e293b' }}>
                        <th className="text-left px-4 py-3 font-semibold" style={{ color: '#94a3b8' }}>IP</th>
                        <th className="text-left px-4 py-3 font-semibold" style={{ color: '#94a3b8' }}>{t('analytics.col_status')}</th>
                        <th className="text-left px-4 py-3 font-semibold" style={{ color: '#94a3b8' }}>{t('analytics.col_reason')}</th>
                        <th className="text-left px-4 py-3 font-semibold" style={{ color: '#94a3b8' }}>{t('analytics.col_blocked_by')}</th>
                        <th className="text-left px-4 py-3 font-semibold" style={{ color: '#94a3b8' }}>{t('analytics.col_date')}</th>
                        <th className="text-center px-4 py-3 font-semibold" style={{ color: '#94a3b8' }}>{t('analytics.col_action')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {blockedIps.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-12" style={{ color: '#475569' }}>
                          <ShieldCheck className="w-8 h-8 mx-auto mb-2" style={{ color: '#334155' }} />
                          {t('analytics.no_blocked_ips')}
                        </td></tr>
                      ) : blockedIps.map((b, i) => (
                        <tr key={i} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                          <td className="px-4 py-3 font-mono font-bold" style={{ color: b.active ? '#ef4444' : '#64748b' }}>{b.ip}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                              style={{
                                background: b.active ? 'rgba(239,68,68,0.15)' : 'rgba(100,116,139,0.15)',
                                color: b.active ? '#ef4444' : '#64748b',
                              }}>
                              {b.active ? t('analytics.status_blocked') : t('analytics.status_inactive')}
                            </span>
                          </td>
                          <td className="px-4 py-3" style={{ color: '#cbd5e1' }}>{b.reason || '-'}</td>
                          <td className="px-4 py-3" style={{ color: '#94a3b8' }}>{b.blocked_by || '-'}</td>
                          <td className="px-4 py-3" style={{ color: '#94a3b8' }}>{fmtDate(b.blocked_at)}</td>
                          <td className="px-4 py-3 text-center">
                            {b.active ? (
                              <button onClick={() => handleUnblockIp(b.ip)}
                                disabled={blockingIp === b.ip}
                                className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:scale-105 disabled:opacity-50"
                                style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }}
                                data-testid={`unblock-blocked-${b.ip}`}>
                                <ShieldCheck className="w-3 h-3 inline mr-1" />
                                {blockingIp === b.ip ? '...' : t('analytics.unblock')}
                              </button>
                            ) : (
                              <button onClick={() => handleBlockIp(b.ip)}
                                disabled={blockingIp === b.ip}
                                className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:scale-105 disabled:opacity-50"
                                style={{ background: 'rgba(239,68,68,0.1)', color: '#94a3b8', border: '1px solid #334155' }}
                                data-testid={`reblock-${b.ip}`}>
                                {blockingIp === b.ip ? '...' : t('analytics.reblock')}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ─── IP Alerts Tab ─── */}
          {tab === 'alerts' && (
            <div className="space-y-5" data-testid="alerts-tab">
              {/* Alert Config */}
              <div className={`${staffCardClass} p-5`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <Settings2 className="w-4 h-4 text-[var(--theme-primary)]" /> {t('analytics.alert_config_title')}
                  </h3>
                  <div className="flex items-center gap-2">
                    <button onClick={checkNow} disabled={checkingNow}
                      className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:scale-105 disabled:opacity-50 flex items-center gap-1.5"
                      style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}
                      data-testid="check-now-btn">
                      <Shield className="w-3 h-3" /> {checkingNow ? t('analytics.checking') : t('analytics.check_now')}
                    </button>
                    <button onClick={saveAlertConfig} disabled={savingConfig}
                      className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:scale-105 disabled:opacity-50"
                      style={{ background: 'rgba(124,58,237,0.9)', color: '#fff' }}
                      data-testid="save-alert-config-btn">
                      {savingConfig ? t('analytics.saving') : t('analytics.save')}
                    </button>
                  </div>
                </div>

                {alertConfig && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="flex items-center gap-3 p-3 rounded-xl" style={{ background: '#1e293b' }}>
                      <input type="checkbox" checked={alertConfig.enabled}
                        onChange={e => setAlertConfig({ ...alertConfig, enabled: e.target.checked })}
                        className="w-4 h-4 rounded accent-purple-500" data-testid="alert-enabled-check" />
                      <div>
                        <span className="text-sm text-white font-medium">{t('analytics.alerts_enabled')}</span>
                        <p className="text-[10px] mt-0.5" style={{ color: '#64748b' }}>{t('analytics.alerts_enabled_desc')}</p>
                      </div>
                    </label>
                    <div className="p-3 rounded-xl" style={{ background: '#1e293b' }}>
                      <label className="text-[11px] font-bold uppercase" style={{ color: '#64748b' }}>{t('analytics.threshold_label')}</label>
                      <input type="number" min="10" value={alertConfig.threshold || 50}
                        onChange={e => setAlertConfig({ ...alertConfig, threshold: parseInt(e.target.value) || 50 })}
                        className="w-full mt-1 px-3 py-2 rounded-lg text-sm"
                        style={{ background: '#0f172a', color: '#f1f5f9', border: '1px solid #334155' }}
                        data-testid="alert-threshold-input" />
                    </div>
                    <div className="p-3 rounded-xl" style={{ background: '#1e293b' }}>
                      <label className="text-[11px] font-bold uppercase" style={{ color: '#64748b' }}>{t('analytics.alert_email_label')}</label>
                      <input type="email" value={alertConfig.email || ''}
                        onChange={e => setAlertConfig({ ...alertConfig, email: e.target.value })}
                        className="w-full mt-1 px-3 py-2 rounded-lg text-sm"
                        style={{ background: '#0f172a', color: '#f1f5f9', border: '1px solid #334155' }}
                        placeholder="email@example.com"
                        data-testid="alert-email-input" />
                    </div>
                    <label className="flex items-center gap-3 p-3 rounded-xl" style={{ background: '#1e293b' }}>
                      <input type="checkbox" checked={alertConfig.auto_block}
                        onChange={e => setAlertConfig({ ...alertConfig, auto_block: e.target.checked })}
                        className="w-4 h-4 rounded accent-red-500" data-testid="alert-autoblock-check" />
                      <div>
                        <span className="text-sm text-white font-medium">{t('analytics.autoblock')}</span>
                        <p className="text-[10px] mt-0.5" style={{ color: '#64748b' }}>{t('analytics.autoblock_desc')}</p>
                      </div>
                    </label>
                  </div>
                )}
              </div>

              {/* Alert History */}
              <div className="rounded-2xl border overflow-hidden" style={{ background: '#0f172a', borderColor: '#1e293b' }}>
                <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: '#1e293b' }}>
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <Bell className="w-4 h-4" style={{ color: '#f59e0b' }} /> {t('analytics.alert_history_title')}
                  </h3>
                  <span className="text-[10px] font-bold px-2 py-1 rounded-lg" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
                    {t('analytics.alerts_count', { count: alertHistory.length })}
                  </span>
                </div>
                {alertHistory.length === 0 ? (
                  <div className="p-8 text-center">
                    <ShieldCheck className="w-10 h-10 mx-auto mb-2" style={{ color: '#22c55e' }} />
                    <p className="text-sm" style={{ color: '#64748b' }}>{t('analytics.no_alerts')}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr style={{ background: '#1e293b' }}>
                          <th className="px-4 py-2.5 text-left font-semibold" style={{ color: '#64748b' }}>IP</th>
                          <th className="px-4 py-2.5 text-left font-semibold" style={{ color: '#64748b' }}>{t('analytics.col_visits_per_hour')}</th>
                          <th className="px-4 py-2.5 text-left font-semibold" style={{ color: '#64748b' }}>{t('analytics.col_sites')}</th>
                          <th className="px-4 py-2.5 text-left font-semibold" style={{ color: '#64748b' }}>{t('analytics.col_autoblocked')}</th>
                          <th className="px-4 py-2.5 text-left font-semibold" style={{ color: '#64748b' }}>{t('analytics.col_email')}</th>
                          <th className="px-4 py-2.5 text-left font-semibold" style={{ color: '#64748b' }}>{t('analytics.col_date')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alertHistory.map((a, i) => (
                          <tr key={a.id || i} className="border-t" style={{ borderColor: '#1e293b' }}>
                            <td className="px-4 py-3 font-mono font-bold" style={{ color: '#ef4444' }}>{a.ip}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>
                                {a.visit_count}
                              </span>
                            </td>
                            <td className="px-4 py-3" style={{ color: '#94a3b8' }}>{(a.sites || []).join(', ')}</td>
                            <td className="px-4 py-3">
                              {a.auto_blocked ? (
                                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>{t('analytics.yes')}</span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold" style={{ background: 'rgba(100,116,139,0.15)', color: '#64748b' }}>{t('analytics.no')}</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {a.email_sent ? (
                                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>{t('analytics.sent')}</span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold" style={{ background: 'rgba(100,116,139,0.15)', color: '#64748b' }}>-</span>
                              )}
                            </td>
                            <td className="px-4 py-3" style={{ color: '#94a3b8' }}>{fmtDate(a.triggered_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Block confirmation modal ─── */}
          {confirmBlock && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="block-confirm-modal">
              <div className={`${staffCardClass} p-6 w-full max-w-md mx-4`} data-testid="block-confirm-modal-inner">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-red-500/15">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">{t('analytics.confirm_block_title')}</h3>
                    <p className="text-xs mt-0.5 text-gray-500">
                      {t('analytics.confirm_block_ip_prefix')}<span className="font-mono text-red-400">{confirmBlock}</span>{t('analytics.confirm_block_ip_suffix')}
                    </p>
                  </div>
                </div>
                <input
                  value={blockReason}
                  onChange={e => setBlockReason(e.target.value)}
                  placeholder={t('analytics.block_reason_placeholder')}
                  className={`${staffInputClass} mb-4`}
                  data-testid="block-reason-input"
                />
                <div className="flex items-center gap-2 justify-end">
                  <button type="button" onClick={() => { setConfirmBlock(null); setBlockReason(''); }}
                    className={staffSecondaryBtnClass}
                    data-testid="block-cancel-btn">
                    {t('analytics.cancel')}
                  </button>
                  <button type="button" onClick={() => handleBlockIp(confirmBlock)}
                    disabled={blockingIp}
                    className="px-4 py-2 rounded-xl text-sm font-bold transition-all disabled:opacity-50 bg-red-600 hover:bg-red-500 text-white"
                    data-testid="block-confirm-btn">
                    <Ban className="w-4 h-4 inline mr-1" />
                    {blockingIp ? t('analytics.blocking') : t('analytics.confirm_block')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
    </Layout>
  );
}
